import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Brand } from "./Brand";
import { cn } from "@/shared/utils/cn";
import { Button, type ButtonProps } from "../shadcn/Button";
import { navigationState } from "@/core/routing/navigationPolicy";

export function PageHeaderAction({ className, ...props }: Omit<ButtonProps, "variant">) {
  return <Button {...props} variant="ghost" className={cn("min-h-11 text-primary [&>svg]:hidden", className)} />;
}

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /** Off for standalone pages that must not lead into the rest of the app. */
  homeLink?: boolean;
}

export function PageHeader({
  title,
  description,
  actions,
  children,
  homeLink = true,
}: PageHeaderProps) {
  const { businessId, locationId } = useParams();
  const home = businessId && locationId ? `/b/${businessId}/l/${locationId}` : "/";
  const brand = <Brand className="[&>span:first-child]:text-[45px]" />;
  return (
    <header className="mx-auto w-full max-w-[960px] shrink-0 text-foreground">
      <div className="flex min-h-16 items-start gap-2 px-4 py-2 sm:gap-4 sm:px-[30px]">
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
          {homeLink ? (
            <Link to={home} state={navigationState("start")} aria-label="Froppio home" className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl">
              {brand}
            </Link>
          ) : (
            <span className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">{brand}</span>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="break-words text-xl font-extrabold leading-tight">{title}</h1>
            {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {actions && <div className="flex min-h-[60px] shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children && <div className="px-4 pb-3 sm:px-[30px]">{children}</div>}
    </header>
  );
}
